import React, { useEffect, useRef, useState } from 'react';
import { FaCommentDots, FaTimes, FaPaperPlane, FaRegCopy } from 'react-icons/fa';
import api from '../api';
import './Asistente.css';

const PERFILES = {
  vendedor: {
    titulo: 'Asistente de Ventas',
    bienvenida: 'Hola, soy el asistente de AITA. Puedo ayudarte con tus solicitudes de venta, con las existencias de artículos y a consultar el adeudo de un cliente.',
    // etiqueta = lo que se ve en el botón; texto = lo que se precarga en el campo (puede quedar por completar)
    sugerencias: [
      { etiqueta: '¿Cómo van mis solicitudes?', texto: '¿Cómo van mis solicitudes?' },
      { etiqueta: 'Ver qué le falta a una solicitud condicionada', texto: '¿Qué le falta a mi solicitud condicionada?' },
      { etiqueta: 'Consultar existencias y precio de un artículo', texto: '¿Hay existencias de ' },
      { etiqueta: 'Consultar a un cliente antes de venderle', texto: '¿Cómo está el cliente ' },
    ],
  },
  cobranza: {
    titulo: 'Asistente de Cobranza',
    bienvenida: 'Hola, soy el asistente de AITA. Puedo consultar el adeudo y las gestiones de un cliente y redactar borradores de mensajes de cobro.',
    sugerencias: [
      { etiqueta: 'Consultar el adeudo de un cliente', texto: '¿Cuánto debe el cliente ' },
      { etiqueta: 'Redactar un mensaje de cobro', texto: 'Redacta un mensaje de cobro para el cliente ' },
      { etiqueta: 'Ver las últimas gestiones de un cliente', texto: '¿Qué gestiones tiene registradas el cliente ' },
    ],
  },
  compras: {
    titulo: 'Asistente de Compras',
    bienvenida: 'Hola, soy el asistente de AITA. Puedo consultar requisiciones, cotizaciones, solicitudes por autorizar, existencias, precios de la competencia y el historial de compras, y buscar en internet dónde comprar un artículo.',
    sugerencias: [
      { etiqueta: '¿Qué requisiciones hay por comprar?', texto: '¿Qué requisiciones hay por comprar?' },
      { etiqueta: 'Recomendar dónde comprar una requisición', texto: 'Recomiéndame dónde comprar la requisición REQ-2026-' },
      { etiqueta: '¿Qué cotizaciones están pendientes?', texto: '¿Qué cotizaciones están pendientes de cotizar?' },
      { etiqueta: '¿Qué solicitudes están por autorizar?', texto: '¿Qué solicitudes están por autorizar?' },
      { etiqueta: 'Consultar el precio de la competencia', texto: '¿Cuánto cuesta en la competencia ' },
    ],
  },
};

// Convierte las direcciones web del texto (por ejemplo las fuentes de una búsqueda en internet) en enlaces
// que abren en otra pestaña. El texto sigue siendo texto: React lo escapa, no se interpreta HTML.
function conEnlaces(texto) {
  return String(texto).split(/(https?:\/\/[^\s<>"]+)/g).map((trozo, i) => {
    if (!/^https?:\/\//.test(trozo)) return trozo;
    const url = trozo.replace(/[.,;:!?)\]]+$/, '');          // la puntuación final no es parte de la dirección
    const resto = trozo.slice(url.length);
    return (
      <React.Fragment key={i}>
        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>{resto}
      </React.Fragment>
    );
  });
}

// Mismo corte que el @media de Asistente.css
const esMovil = () => Boolean(window.matchMedia?.('(max-width: 600px)').matches);

function leerToken() {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
}

function mensajeDeError(err) {
  const datos = err?.response?.data;
  if (datos?.error) return datos.error;
  if (err?.sinConexion) return 'Sin conexión a internet. Revisa tu red e intenta de nuevo.';
  return 'No pude responder en este momento. Intenta de nuevo.';
}

export default function Asistente({ rol }) {
  const perfil = PERFILES[rol];
  const [abierto, setAbierto] = useState(false);
  const [mensajes, setMensajes] = useState([]);
  const [entrada, setEntrada] = useState('');
  const [cargando, setCargando] = useState(false);
  const [restantes, setRestantes] = useState(null);
  const [disponible, setDisponible] = useState(true);
  const [copiado, setCopiado] = useState(null);
  const panelRef = useRef(null);
  const finRef = useRef(null);
  const entradaRef = useRef(null);
  const estadoConsultado = useRef(false);

  // Marca el body mientras el botón del asistente está en pantalla: otras pantallas con botones
  // flotantes en la esquina inferior derecha (p. ej. compartir estado de cuenta) se acomodan con esto
  useEffect(() => {
    if (!perfil) return undefined;
    document.body.classList.add('con-asistente');
    return () => document.body.classList.remove('con-asistente');
  }, [perfil]);

  useEffect(() => {
    if (!abierto || estadoConsultado.current) return;
    estadoConsultado.current = true;
    api
      .get('/asistente/estado', { headers: { Authorization: `Bearer ${leerToken()}` } })
      .then(({ data }) => {
        setDisponible(Boolean(data.disponible));
        setRestantes(data.restantes);
      })
      .catch(() => setDisponible(false));
  }, [abierto]);

  useEffect(() => {
    if (abierto) finRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [mensajes, cargando, abierto]);

  useEffect(() => {
    // En el celular no se enfoca solo: el teclado taparía la bienvenida y la respuesta
    if (abierto && !cargando && !esMovil()) entradaRef.current?.focus();
  }, [abierto, cargando]);

  // Celular: el panel ocupa el área visible real (sigue al teclado) y la página de atrás no se desplaza
  useEffect(() => {
    if (!abierto || !esMovil()) return undefined;
    const panel = panelRef.current;
    const vv = window.visualViewport;
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const ajustar = () => {
      if (!vv || !panel) return;
      panel.style.setProperty('--asistente-alto', `${vv.height}px`);
      panel.style.setProperty('--asistente-arriba', `${vv.offsetTop}px`);
    };
    const alRedimensionar = () => {
      ajustar();
      finRef.current?.scrollIntoView({ block: 'end' });
    };
    ajustar();
    vv?.addEventListener('resize', alRedimensionar);
    vv?.addEventListener('scroll', ajustar);

    return () => {
      vv?.removeEventListener('resize', alRedimensionar);
      vv?.removeEventListener('scroll', ajustar);
      document.body.style.overflow = overflowPrevio;
    };
  }, [abierto]);

  if (!perfil) return null;

  const sinCupo = restantes === 0;
  const bloqueado = !disponible || sinCupo;

  async function enviar(texto) {
    const limpio = (texto ?? entrada).trim();
    if (!limpio || cargando || bloqueado) return;

    const nuevos = [...mensajes, { rol: 'usuario', texto: limpio }];
    setMensajes(nuevos);
    setEntrada('');
    setCargando(true);

    try {
      // Los avisos de error no forman parte de la conversación que se reenvía.
      const historial = nuevos.filter(m => !m.error).map(({ rol: r, texto: t }) => ({ rol: r, texto: t }));
      const { data } = await api.post(
        '/asistente/chat',
        { mensajes: historial },
        { headers: { Authorization: `Bearer ${leerToken()}` } }
      );
      setMensajes(previos => [...previos, { rol: 'asistente', texto: data.respuesta }]);
      if (typeof data.restantes === 'number') setRestantes(data.restantes);
    } catch (err) {
      setMensajes(previos => [...previos, { rol: 'asistente', texto: mensajeDeError(err), error: true }]);
      if (typeof err?.response?.data?.restantes === 'number') setRestantes(err.response.data.restantes);
      if (err?.response?.status === 503) setDisponible(false);
    } finally {
      setCargando(false);
    }
  }

  function alTeclear(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  }

  async function copiar(texto, indice) {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(indice);
      setTimeout(() => setCopiado(actual => (actual === indice ? null : actual)), 1800);
    } catch {
      setCopiado(null);
    }
  }

  if (!abierto) {
    return (
      <button
        type="button"
        className="asistente-fab"
        onClick={() => setAbierto(true)}
        aria-label="Abrir el asistente"
        title="Asistente"
      >
        <FaCommentDots size={22} />
        <span className="asistente-fab-texto">Asistente</span>
      </button>
    );
  }

  return (
    <section ref={panelRef} className="asistente-panel" role="dialog" aria-label={perfil.titulo}>
      <header className="asistente-cabecera">
        <div>
          <strong>{perfil.titulo}</strong>
          <small>Consulta información de solo lectura</small>
        </div>
        <button type="button" className="asistente-cerrar" onClick={() => setAbierto(false)} aria-label="Cerrar el asistente">
          <FaTimes />
        </button>
      </header>

      <div className="asistente-mensajes">
        {mensajes.length === 0 && (
          <div className="asistente-bienvenida">
            <p>{perfil.bienvenida}</p>
            <div className="asistente-sugerencias">
              {perfil.sugerencias.map(s => (
                <button
                  key={s.etiqueta}
                  type="button"
                  className="asistente-sugerencia"
                  onClick={() => { setEntrada(s.texto); entradaRef.current?.focus(); }}
                  disabled={bloqueado}
                >
                  {s.etiqueta}
                </button>
              ))}
            </div>
          </div>
        )}

        {mensajes.map((m, i) => (
          <div key={i} className={`asistente-burbuja asistente-burbuja--${m.rol}${m.error ? ' asistente-burbuja--error' : ''}`}>
            <div className="asistente-texto">{m.rol === 'asistente' ? conEnlaces(m.texto) : m.texto}</div>
            {m.rol === 'asistente' && !m.error && (
              <button type="button" className="asistente-copiar" onClick={() => copiar(m.texto, i)}>
                <FaRegCopy /> {copiado === i ? 'Copiado' : 'Copiar'}
              </button>
            )}
          </div>
        ))}

        {cargando && (
          <div className="asistente-burbuja asistente-burbuja--asistente asistente-escribiendo">
            Consultando...
          </div>
        )}
        <div ref={finRef} />
      </div>

      <footer className="asistente-pie">
        {!disponible && <p className="asistente-aviso">El asistente no está disponible por ahora.</p>}
        {disponible && sinCupo && <p className="asistente-aviso">Llegaste a tu límite de mensajes de hoy. Mañana podrás seguir usando el asistente.</p>}
        <div className="asistente-entrada">
          <textarea
            ref={entradaRef}
            value={entrada}
            onChange={e => setEntrada(e.target.value)}
            onKeyDown={alTeclear}
            placeholder="Escribe tu pregunta..."
            rows={2}
            maxLength={2000}
            disabled={cargando || bloqueado}
          />
          <button
            type="button"
            className="asistente-enviar"
            onClick={() => enviar()}
            disabled={cargando || bloqueado || !entrada.trim()}
            aria-label="Enviar"
          >
            <FaPaperPlane />
          </button>
        </div>
        <small className="asistente-nota">
          {typeof restantes === 'number' && disponible ? `Te quedan ${restantes} mensajes hoy. ` : ''}
          El asistente puede equivocarse: verifica los datos importantes.
        </small>
      </footer>
    </section>
  );
}
